import { computed, ref, watch, type ComputedRef } from 'vue'

import { runStageAssistantTurn } from '@/api/workflow'
import type { ApprovalStage, WorkflowStageAssistantAction } from '@/types/workflow'

import type {
  StageAssistantMessage,
  StageAssistantRegistry,
  StageAssistantSessionState,
  StageAssistantSideEffectResult,
} from './stageAssistantTypes'

interface UseStageAssistantOptions {
  projectId: number
  currentStage: ComputedRef<ApprovalStage>
  currentStageLabel: ComputedRef<string>
  buildRegistry: (instruction?: string) => StageAssistantRegistry
  runSideEffectAction?: (action: WorkflowStageAssistantAction, registry: StageAssistantRegistry) => Promise<StageAssistantSideEffectResult>
}

function buildWelcomeMessage(stageLabel: string): StageAssistantMessage {
  return {
    id: `stage-assistant-welcome-${stageLabel}`,
    role: 'assistant',
    content: `我会只围绕当前的${stageLabel}页面工作。你直接说修改意图、风格要求或问题，我会理解后回填可执行字段，再告诉你处理结果。`,
  }
}

function buildAssistantReply(
  message: string,
  summary: { applied: Array<{ label: string }>; skipped: Array<{ label: string }> },
  sideEffectMessages: string[],
) {
  const trimmed = String(message || '').trim()
  const parts = [trimmed]

  if (summary.applied.length) {
    parts.push(`已同步到：${summary.applied.map((item) => item.label).join('、')}`)
  }

  if (sideEffectMessages.length) {
    parts.push(sideEffectMessages.join('\n'))
  }

  if (summary.skipped.length) {
    parts.push(`未执行：${summary.skipped.map((item) => item.label).join('、')}`)
  }

  return parts.filter(Boolean).join('\n\n')
}

function buildStageAssistantErrorMessage(error: any) {
  const rawMessage = String(error?.message || '').trim()
  if (!rawMessage) {
    return '当前阶段助手执行失败，请稍后重试。'
  }

  const normalized = rawMessage.toLowerCase()
  if (normalized.includes('timeout')) {
    return '当前阶段助手本轮理解超时了。我已经把上下文收窄到当前局部字段，你可以直接重试；如果还是超时，请把要求拆成更小的一条一条说。'
  }

  return rawMessage
}

export function useStageAssistant(options: UseStageAssistantOptions): StageAssistantSessionState {
  const open = ref(false)
  const draft = ref('')
  const loading = ref(false)
  const sessions = ref<Partial<Record<ApprovalStage, StageAssistantMessage[]>>>({})

  function ensureStageSession(stage: ApprovalStage) {
    if (!sessions.value[stage]?.length) {
      sessions.value = {
        ...sessions.value,
        [stage]: [buildWelcomeMessage(options.currentStageLabel.value)],
      }
    }
  }

  watch(
    () => options.currentStage.value,
    (stage) => {
      ensureStageSession(stage)
      draft.value = ''
    },
    { immediate: true },
  )

  const messages = computed(() => {
    ensureStageSession(options.currentStage.value)
    return sessions.value[options.currentStage.value] || []
  })

  const title = computed(() => `${options.currentStageLabel.value}对话助手`)
  const description = computed(() => `理解你在${options.currentStageLabel.value}页面上的自然语言要求，并把可执行结果同步到当前阶段字段。`)
  const helperText = computed(() => options.buildRegistry(draft.value).currentFocus)

  async function submit() {
    const instruction = draft.value.trim()
    if (!instruction || loading.value) {
      return
    }

    ensureStageSession(options.currentStage.value)
    const nextUserMessage: StageAssistantMessage = {
      id: `stage-assistant-user-${Date.now()}`,
      role: 'user',
      content: instruction,
    }
    messages.value.push(nextUserMessage)
    draft.value = ''
    loading.value = true
    open.value = true

    const registry = options.buildRegistry(instruction)

    try {
      const response = await runStageAssistantTurn({
        project_id: options.projectId,
        stage: registry.stage,
        stage_label: registry.stageLabel,
        user_message: instruction,
        current_focus: registry.currentFocus,
        fields: registry.fields,
        conversation: messages.value.slice(-6).map((message) => ({
          role: message.role,
          content: message.content,
        })),
      })

      const fieldActions = (response.actions || []).filter((action) => action.action_type === 'set_field')
      const sideEffectActions = (response.actions || []).filter((action) => action.action_type !== 'set_field')
      const summary = registry.applyActions(fieldActions)
      const sideEffectMessages: string[] = []

      for (const action of sideEffectActions) {
        const result = await options.runSideEffectAction?.(action, registry)
        if (result?.handled && result.message) {
          sideEffectMessages.push(result.message)
        }
      }

      messages.value.push({
        id: `stage-assistant-answer-${Date.now()}`,
        role: 'assistant',
        content: buildAssistantReply(response.assistant_message, summary, sideEffectMessages),
      })
    } catch (error: any) {
      messages.value.push({
        id: `stage-assistant-error-${Date.now()}`,
        role: 'assistant',
        content: buildStageAssistantErrorMessage(error),
      })
    } finally {
      loading.value = false
    }
  }

  return {
    open,
    draft,
    loading,
    messages,
    title,
    description,
    helperText,
    submit,
  }
}